import { useEffect, useState, useRef } from 'react';

export default function ComplianceReadiness() {
  const [animated, setAnimated] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const controls = [
    {
      framework: 'SOC 2',
      area: 'Security',
      description: 'Ephemeral compute, zero persistent storage of client data, AES-256 encryption in transit and at rest, access control logging, and incident response procedures — aligned to SOC 2 Type II Trust Services Criteria from day one.',
    },
    {
      framework: 'SOC 2',
      area: 'Availability',
      description: 'Serverless Modal infrastructure with independent scaling, redundant processing paths, and uptime monitoring. No single points of failure in the processing pipeline.',
    },
    {
      framework: 'SOC 2',
      area: 'Confidentiality',
      description: 'Client data is never written to disk on our servers. Legal content exists only in memory during processing, then destroyed. There is nothing to retain, nothing to leak.',
    },
    {
      framework: 'ISO 27001',
      area: 'Information Security Management',
      description: 'Architecture follows ISO/IEC 27001 Annex A controls: asset management limited to metadata, access control enforced at every endpoint, cryptographic controls applied to all data in transit, and supplier relationships governed by ZDR requirements.',
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-24 bg-[#09090b] border-t border-[#1a1a1c]">
      <div className="max-w-[1200px] mx-auto px-6 sm:px-8">

        <div className={`text-center mb-16 sm:mb-20 transition-all duration-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="font-harvey-sans text-[#06b6d4] text-xs font-semibold tracking-widest uppercase mb-4">
            Enterprise Compliance
          </p>
          <h2 className="font-harvey-serif text-white text-3xl sm:text-[42px] font-normal tracking-tight mb-6">
            Built for Audit. From Day One.
          </h2>
          <p className="font-harvey-sans text-[#a1a1aa] text-lg sm:text-xl font-light tracking-wide max-w-[700px] mx-auto leading-relaxed">
            HushLegal was architected in line with SOC 2 and ISO 27001 parameters. We did not bolt compliance on afterward. The architecture itself is the compliance posture.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-[1000px] mx-auto mb-12 sm:mb-16">
          {controls.map((control, index) => (
            <div
              key={index}
              className={`p-6 sm:p-8 bg-[#0f0f11] border border-[#1a1a1c] rounded-xl hover:border-[#06b6d4]/40 hover:shadow-[0_0_30px_rgba(6,182,212,0.08)] transition-all duration-700`}
              style={{ transitionDelay: `${index * 150}ms`, opacity: animated ? 1 : 0, transform: animated ? 'translateY(0)' : 'translateY(24px)' }}
            >
              <div className="flex items-start gap-3 mb-4">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-[#06b6d4]/10 border border-[#06b6d4]/20 text-[#06b6d4] text-xs font-harvey-sans font-semibold tracking-widest uppercase whitespace-nowrap">
                  {control.framework}
                </span>
                <span className="font-harvey-sans text-white text-sm font-semibold tracking-wide mt-0.5">
                  {control.area}
                </span>
              </div>
              <p className="font-harvey-sans text-[#a1a1aa] text-[15px] font-light leading-relaxed tracking-wide">
                {control.description}
              </p>
            </div>
          ))}
        </div>

        <div
          className={`max-w-[800px] mx-auto p-6 sm:p-8 bg-[#0f0f11] border border-[#06b6d4]/20 rounded-xl text-center transition-all duration-700 delay-700 ${animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="font-harvey-sans text-[#d4d4d8] text-base sm:text-lg font-light leading-relaxed tracking-wide mb-3">
            When you're ready for a formal SOC 2 Type II audit, HushLegal is ready too.
          </p>
          <p className="font-harvey-sans text-[#a1a1aa] text-sm font-light tracking-wide">
            Enterprise inquiries and compliance documentation available upon request.{' '}
            <a href="mailto:security@hushlegal.com" className="text-[#06b6d4] hover:underline">security@hushlegal.com</a>
          </p>
        </div>

        <p
          className={`font-harvey-serif text-[#06b6d4] text-xl sm:text-2xl font-normal tracking-tight text-center mt-12 sm:mt-16 transition-all duration-700 delay-900 ${animated ? 'opacity-100' : 'opacity-0'}`}
        >
          Compliance is not a checkbox. It is a consequence of how we build
        </p>

      </div>
    </section>
  );
}
